import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import notificationApi from '../../services/notificationApi';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Create as CreateIcon,
  Share as ShareIcon,
  Notifications as NotificationsIcon,
  Payment as PaymentIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Home as HomeIcon,
  TrendingUp as TrendingUpIcon,
  SmartToy as SmartToyIcon,
  Article as ArticleIcon,
  Public as PublicIcon,
  Campaign as CampaignIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  Timeline as TimelineIcon,
  Security as SecurityIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AttachMoney as PricingIcon,
} from '@mui/icons-material';
import Logo from '../common/Logo';

interface LayoutProps {
  children: React.ReactNode;
  darkMode?: boolean;
  onToggleDarkMode?: () => void;
}

const drawerWidth = 280;
const miniDrawerWidth = 72;

const navigationItems = [
  { text: 'Home', icon: <HomeIcon />, path: '/', category: 'main' },
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', category: 'main' },
  { text: 'Create PR', icon: <CreateIcon />, path: '/create-pr', category: 'main' },
  { text: 'PR Library', icon: <ArticleIcon />, path: '/pr-library', category: 'main' },
  { text: 'Distribute', icon: <ShareIcon />, path: '/distribute', category: 'main' },
  { text: 'Campaigns', icon: <CampaignIcon />, path: '/campaigns', category: 'main' },
  { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics', category: 'main' },
  // { text: 'Media Contacts', icon: <PeopleIcon />, path: '/media-contacts', category: 'main' },
  { text: 'Publications', icon: <PublicIcon />, path: '/publications', category: 'main' },
  { text: 'Verification', icon: <SecurityIcon />, path: '/verification', category: 'main' },
  { text: 'Pricing', icon: <PricingIcon />, path: '/pricing', category: 'main' },
  { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications', category: 'secondary' },
  { text: 'Payments', icon: <PaymentIcon />, path: '/payments', category: 'secondary' },
  { text: 'PR Credits', icon: <PaymentIcon />, path: '/credits', category: 'secondary' },
  { text: 'Reports', icon: <AssessmentIcon />, path: '/reports', category: 'secondary' },
  { text: 'Timeline', icon: <TimelineIcon />, path: '/timeline', category: 'secondary' },
  { text: 'Advanced', icon: <SmartToyIcon />, path: '/advanced', category: 'secondary' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings', category: 'secondary' },
  { text: 'Help & Support', icon: <HelpIcon />, path: '/help', category: 'secondary' },
];

const Layout: React.FC<LayoutProps> = ({
  children,
  darkMode = false,
  onToggleDarkMode,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Fetch unread notification count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationApi.getUserNotifications(1, 1, true);
      if (response.success) {
        setUnreadNotificationCount(response.pagination.total);
      }
    } catch (error) {
      console.error('Failed to fetch unread notification count:', error);
      // Don't set error state, just silently fail
    }
  }, []);

  // Fetch unread count on mount and set up polling
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Poll every 30 seconds for new notifications
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchUnreadCount]);

  // Refresh count when navigating to/from notifications page
  useEffect(() => {
    if (location.pathname === '/notifications' || location.pathname !== '/notifications') {
      fetchUnreadCount();
    }
  }, [location.pathname, fetchUnreadCount]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
    handleProfileMenuClose();
  };

  const currentDrawerWidth = isMobile ? drawerWidth : desktopOpen ? drawerWidth : miniDrawerWidth;

  const drawer = (isFullDrawer: boolean) => (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Logo Section */}
      {isFullDrawer ? (
        <>
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              transition: 'all 0.3s ease',
              minHeight: 100,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <Logo size="medium" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Get PR on Demand
            </Typography>
          </Box>
          <Divider />
        </>
      ) : (
        <Box sx={{ height: 64 }} />
      )}

      {/* Navigation */}
      <List
        sx={{
          flex: 1,
          pl: isFullDrawer ? 2 : 0.5,
          pr: isFullDrawer ? 2 : 0.5,
          pt: 1,
          pb: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
            width: 0,
            height: 0,
          },
        }}
      >
        {/* Main Navigation */}
        {isFullDrawer && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              ml: 2,
              mb: 1,
              display: 'block',
            }}
          >
            Main
          </Typography>
        )}
        {navigationItems
          .filter((item) => item.category === 'main')
          .map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={!isFullDrawer ? item.text : ''} placement="right" arrow>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: location.pathname === item.path ? 'primary.main' : 'transparent',
                    color: location.pathname === item.path ? 'primary.contrastText' : 'text.primary',
                    justifyContent: isFullDrawer ? 'flex-start' : 'center',
                    pl: isFullDrawer ? 2 : 0.5,
                    pr: isFullDrawer ? 2 : 0.5,
                    minHeight: 40,
                    '&:hover': {
                      backgroundColor: location.pathname === item.path 
                        ? 'primary.dark' 
                        : 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isFullDrawer ? 40 : 'auto',
                      color: location.pathname === item.path ? 'primary.contrastText' : 'text.secondary',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {item.text === 'Notifications' ? (
                      <Badge 
                        badgeContent={unreadNotificationCount > 0 ? unreadNotificationCount : undefined} 
                        color="error" 
                        variant={unreadNotificationCount > 0 ? "standard" : "dot"}
                        max={99}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  {isFullDrawer && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === item.path ? 600 : 500,
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}

        <Divider sx={{ my: 2 }} />

        {/* Secondary Navigation */}
        {isFullDrawer && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              ml: 2,
              mb: 1,
              display: 'block',
            }}
          >
            Tools & Settings
          </Typography>
        )}
        {navigationItems
          .filter((item) => item.category === 'secondary')
          .map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={!isFullDrawer ? item.text : ''} placement="right" arrow>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: location.pathname === item.path ? 'primary.main' : 'transparent',
                    color: location.pathname === item.path ? 'primary.contrastText' : 'text.primary',
                    justifyContent: isFullDrawer ? 'flex-start' : 'center',
                    pl: isFullDrawer ? 2 : 0.5,
                    pr: isFullDrawer ? 2 : 0.5,
                    minHeight: 40,
                    '&:hover': {
                      backgroundColor: location.pathname === item.path 
                        ? 'primary.dark' 
                        : 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isFullDrawer ? 40 : 'auto',
                      color: location.pathname === item.path ? 'primary.contrastText' : 'text.secondary',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {item.text === 'Notifications' ? (
                      <Badge 
                        badgeContent={unreadNotificationCount > 0 ? unreadNotificationCount : undefined} 
                        color="error" 
                        variant={unreadNotificationCount > 0 ? "standard" : "dot"}
                        max={99}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  {isFullDrawer && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === item.path ? 600 : 500,
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
      </List>

      <Divider />

      {/* Theme Toggle */}
      <Box sx={{ p: isFullDrawer ? 2 : '12px 8px', textAlign: 'center' }}>
        {isFullDrawer ? (
          <FormControlLabel
            control={
              <Switch
                checked={darkMode}
                onChange={onToggleDarkMode}
                icon={<LightModeIcon />}
                checkedIcon={<DarkModeIcon />}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {darkMode ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {darkMode ? 'Dark' : 'Light'}
                </Typography>
              </Box>
            }
            sx={{ width: '100%', justifyContent: 'space-between' }}
          />
        ) : (
          <Tooltip
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            placement="right"
            arrow
          >
            <IconButton
              onClick={onToggleDarkMode}
              size="medium"
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              {darkMode ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left rail: only the drawer, responsible for width */}
      <Box
        component="nav"
        sx={{
          width: { xs: 0, md: currentDrawerWidth },
          flexShrink: { xs: 0, md: 0 },
          position: 'relative',
        }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          {drawer(true)}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
              overflowY: 'hidden',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
                width: 0,
                height: 0,
              },
            },
          }}
          open
        >
          {drawer(desktopOpen)}
        </Drawer>

        {/* Collapse/Expand Button (Desktop only, positioned outside drawer) */}
        {!isMobile && (
          <IconButton
            onClick={handleDesktopDrawerToggle}
            sx={{
              position: 'fixed',
              top: 140,
              left: currentDrawerWidth - 16,
              zIndex: theme.zIndex.drawer + 2,
              width: 32,
              height: 32,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              border: '3px solid',
              borderColor: 'background.paper',
              boxShadow: '0 4px 16px rgba(9, 39, 235, 0.5)',
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'scale(1.1)',
                boxShadow: '0 6px 20px rgba(9, 39, 235, 0.7)',
              },
              transition: 'all 0.3s ease, left 0.3s ease',
            }}
          >
            {desktopOpen ? <ChevronLeftIcon sx={{ fontSize: 20 }} /> : <ChevronRightIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        )}
      </Box>

      {/* Right side: AppBar + Main content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* App Bar */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            left: { xs: 0, md: currentDrawerWidth },
            width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
            backgroundColor: darkMode 
              ? 'rgba(18, 18, 18, 0.85)' 
              : 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(20px)',
            borderBottom: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.08)' 
              : '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: darkMode
              ? '0 2px 8px rgba(0, 0, 0, 0.3)'
              : '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
            transition: 'width 0.3s ease, left 0.3s ease, background-color 0.3s ease',
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar 
            sx={{ 
              pl: { xs: 2, md: 3 }, 
              pr: { xs: 2, md: 3 },
              minHeight: '64px !important',
              height: 64,
            }}
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: 2,
                display: { xs: 'block', md: 'none' },
                color: 'text.primary',
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              {(isMobile || !desktopOpen) && (
                <>
                  <Logo size="small" />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      ml: 2, 
                      color: 'text.secondary', 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Get PR on Demand
                  </Typography>
                </>
              )}
            </Box>

            {/* Quick Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
              <Tooltip title="Notifications" arrow>
                <IconButton
                  color="inherit"
                  onClick={() => handleNavigation('/notifications')}
                  sx={{ 
                    color: 'text.primary',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <Badge 
                    badgeContent={unreadNotificationCount} 
                    color="error"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 4px',
                      },
                    }}
                  >
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Tooltip title="Analytics" arrow>
                <IconButton
                  color="inherit"
                  onClick={() => handleNavigation('/analytics')}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                      color: 'text.primary',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <TrendingUpIcon />
                </IconButton>
              </Tooltip>

              {!isMobile && (
                <Button
                  variant="contained"
                  startIcon={<CreateIcon />}
                  sx={{
                    ml: 2,
                    backgroundColor: darkMode ? '#ffffff' : '#000000',
                    color: darkMode ? '#000000' : '#ffffff',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2.5,
                    py: 1,
                    boxShadow: darkMode 
                      ? '0 2px 8px rgba(255, 255, 255, 0.2)' 
                      : '0 2px 8px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.85)',
                      boxShadow: darkMode 
                        ? '0 4px 12px rgba(255, 255, 255, 0.3)' 
                        : '0 4px 12px rgba(0, 0, 0, 0.25)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => handleNavigation('/create-pr')}
                >
                  Create PR
                </Button>
              )}

              <Tooltip title={user ? `${user.firstName} ${user.lastName}` : 'Account'} arrow>
              <IconButton
                  size="medium"
                edge="end"
                aria-label="account of current user"
                aria-controls="profile-menu"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
                  sx={{
                    ml: { xs: 0.5, md: 1 },
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                    },
                    transition: 'background-color 0.2s ease',
                  }}
              >
                  <Avatar 
                    sx={{ 
                      width: 36, 
                      height: 36, 
                      backgroundColor: 'primary.main',
                      border: `2px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'}`,
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}
                  >
                  {user ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : <AccountCircleIcon />}
                </Avatar>
              </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 240,
              borderRadius: 2,
              boxShadow: darkMode
                ? '0 8px 24px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)'
                : '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
              border: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
            },
          }}
        >
          {user && (
            <MenuItem 
              disabled
              sx={{
                py: 1.5,
                px: 2,
                opacity: 1,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: 'primary.main', 
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    border: `2px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'}`,
                  }}
                >
                  {`${user.firstName.charAt(0)}${user.lastName.charAt(0)}`}
                </Avatar>
              </ListItemIcon>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {user.email}
                </Typography>
              </Box>
            </MenuItem>
          )}
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={() => {
              handleNavigation('/settings');
              handleProfileMenuClose();
            }}
            sx={{
              py: 1.25,
              px: 2,
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              },
              transition: 'background-color 0.2s ease',
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Settings
            </Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem 
            onClick={handleLogout}
            sx={{
              py: 1.25,
              px: 2,
              color: 'error.main',
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(211, 47, 47, 0.12)' : 'rgba(211, 47, 47, 0.08)',
              },
              transition: 'background-color 0.2s ease',
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Logout
            </Typography>
          </MenuItem>
        </Menu>

        {/* Main Content */}
        <Box component="main" sx={{ flexGrow: 1, width: '100%', backgroundColor: 'background.default' }}>
          <Toolbar />
          <Box sx={{ p: { xs: 2, md: 3 }, minHeight: 'calc(100vh - 64px)' }}>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
